import React from "react";
import ReactDOM from "react-dom";
import App from "./App.js";
import { queryClient } from "./clients/queryClient.js";
import { QueryClientProvider } from "@tanstack/react-query";

// Suppress "Script error" from iframes in development mode
// This prevents React's error overlay from showing cross-origin iframe errors
if (process.env.NODE_ENV === 'development') {
  // Prevent duplicate registration during HMR
  if (!window.__scriptErrorSuppressionRegistered) {
    /**
     * Checks if an error message is a "Script error" that should be suppressed
     * These are browser-sanitized cross-origin errors from iframes
     * @param {string|Error} errorMessage - The error message or error object
     * @returns {boolean} - True if the error should be suppressed
     */
    const isScriptError = (errorMessage) => {
      const message = typeof errorMessage === 'string'
        ? errorMessage
        : errorMessage?.message || String(errorMessage || '');

      return (
        message === 'Script error.' ||
        message === 'Script error' ||
        message.includes('Script error')
      );
    };

    const originalErrorHandler = window.onerror;
    window.onerror = function (message, source, lineno, colno, error) {
      if (isScriptError(message)) {
        return true; // Suppress the error
      }
      // Call original handler for other errors
      if (originalErrorHandler) {
        return originalErrorHandler(message, source, lineno, colno, error);
      }
      return false;
    };

    // Suppress unhandled promise rejections that are "Script error"
    const handleUnhandledRejection = (event) => {
      if (isScriptError(event?.reason)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    // Patch error event listener to catch iframe errors
    const handleError = (event) => {
      const errorMessage = event?.error?.message || event?.message || '';
      if (isScriptError(errorMessage)) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', handleError, true);

    // Mark as registered to prevent duplicate listeners during HMR
    window.__scriptErrorSuppressionRegistered = true;
  }
}

ReactDOM.render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>,
  document.getElementById('root'),
);
